import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { TabList } from "./TabList/TabList";
import defaultLocales from "./locales";
import { withTranslations } from "../../../utils/withTranslations";
import { Confirm } from "../Confirm/Confirm";
import "./Tabs.scss";

export class UTabs extends React.Component {
  static defaultProps = {
    defaultActiveTabIndex: 0,
  };

  static propTypes = {
    /** Additional class names for the component */
    className: PropTypes.string,
    /** A callback function that allows the parent component to perform an action when a tab is added */
    onAdd: PropTypes.func,
    /** A callback function that allows the parent component to perform an action when a tab is deleted */
    onDelete: PropTypes.func,
    /** A callback function that is called when the active tab is changed */
    onChange: PropTypes.func,
    /** A function the consumer component can use to track the activeTabIndex state */
    children: PropTypes.node,
    /** Allows the parent component to set the default tab */
    defaultActiveTabIndex: PropTypes.number,
    /** Allows the parent component to control the active tab */
    activeTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Locales object for rendering text */
    locales: PropTypes.object,
    /** If true allows for reordering tabs with drag and drop */
    reorderable: PropTypes.bool,
    /** A callback function that returns an array of tabIds in new order from drag and drop */
    onReorder: PropTypes.func,
    /** If true, a confirm modal will show up on the screen for deletion */
    confirmOnDelete: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeTabIndex: props.defaultActiveTabIndex,
      tabList: [],
      confirmModalOpen: false,
      deleteId: null,
      deleteMessage: null,
    };
  }

  componentDidMount() {
    const { children } = this.props;
    this.setState({ tabList: children });
  }

  componentDidUpdate(prevProps) {
    const { activeTabId, children } = this.props;
    const childrenLength = children.length;

    if (children !== prevProps.children) {
      this.setState({ tabList: children });
    }

    if (activeTabId !== prevProps.activeTabId) {
      const activeTabIndex = this.findTabIndex(activeTabId);

      this.setState({ activeTabIndex });
    }

    if (childrenLength > prevProps.children.length) {
      if (prevProps.children.length !== 0) {
        const lastTab = children[childrenLength - 1];

        const lastTabInEditMode = React.cloneElement(lastTab, {
          editMode: true,
        });

        children[childrenLength - 1] = lastTabInEditMode;

        this.setState({ activeTabIndex: childrenLength - 1 });
      }

      this.setState({ tabList: children });
    } else if (childrenLength < prevProps.children.length) {
      this.setState({ tabList: children });
    }
  }

  handleTabChange = (newTabId) => {
    const { onChange } = this.props;
    const { activeTabIndex } = this.state;
    const newActiveTabIndex = this.findTabIndex(newTabId);

    if (activeTabIndex !== newActiveTabIndex && onChange) {
      onChange(newActiveTabIndex);
    }

    this.setState({ activeTabIndex: newActiveTabIndex });
  };

  findTabIndex = (id) => {
    const { tabList } = this.state;
    return tabList.findIndex((tab) => tab.props.id === id);
  };

  onDelete = (id) => {
    const { onDelete } = this.props;
    const { activeTabIndex, tabList } = this.state;

    const tabToDeleteIndex = this.findTabIndex(id);

    const newTabList = tabList.filter((tab) => {
      if (tab.props.id === id) {
        return false;
      }
      return true;
    });

    if (tabToDeleteIndex === activeTabIndex) {
      this.setState({ activeTabIndex: activeTabIndex - 1 });
    }

    if (tabToDeleteIndex === 0) {
      this.setState({ activeTabIndex: 0 });
    } else if (tabToDeleteIndex > activeTabIndex) {
      this.setState({ activeTabIndex });
    } else {
      this.setState({ activeTabIndex: activeTabIndex - 1 });
    }

    this.setState({ tabList: newTabList });

    onDelete(id);

    this.closeConfirmModal();
  };

  openConfirmModal = (id) => {
    const { confirmOnDelete, locales } = this.props;
    const { tabList } = this.state;

    let message;
    const tabIndex = this.findTabIndex(id);
    const tab = tabList[tabIndex].props;
    if (tab.label) {
      message = `Do you want to delete ${tab.label}?`;
    } else {
      message = `Do you want to delete this ${locales.type}?`;
    }

    if (confirmOnDelete) {
      this.setState({
        confirmModalOpen: true,
        deleteId: id,
        deleteMessage: message,
      });
    } else {
      this.onDelete(id);
    }
  };

  closeConfirmModal = () => {
    this.setState({
      confirmModalOpen: false,
      deleteId: null,
      deleteMessage: null,
    });
  };

  renderTabs = () => {
    const { onDelete } = this.props;
    const { activeTabIndex, tabList } = this.state;

    return tabList.map((tab, index) =>
      React.cloneElement(tab, {
        key: index,
        onClick: this.handleTabChange,
        onDelete: onDelete ? this.openConfirmModal : null,
        active: index === activeTabIndex,
      })
    );
  };

  renderActiveTabsChildren = () => {
    const { activeTabIndex, tabList } = this.state;

    if (tabList.length !== 0) {
      return (
        <div className="Tabs__activeTabsChildren">
          {tabList[activeTabIndex].props.children}
        </div>
      );
    }
    return null;
  };

  onReorder = (reorderedTabs, dropIndex) => {
    const { onReorder } = this.props;
    this.setState({ activeTabIndex: dropIndex });
    onReorder(reorderedTabs);
  };

  render() {
    const { className, locales, onAdd, reorderable } = this.props;
    const { confirmModalOpen, deleteId, deleteMessage } = this.state;

    const tabsClass = classNames("Tabs", className);

    return (
      <>
        <div className={tabsClass}>
          <TabList
            onAdd={onAdd}
            addTabToolTip={locales.addTab}
            onReorder={reorderable && this.onReorder}
          >
            {this.renderTabs()}
          </TabList>
          {this.renderActiveTabsChildren()}
        </div>
        <Confirm
          isModalOpen={confirmModalOpen}
          onRequestClose={this.closeConfirmModal}
          onConfirm={this.onDelete}
          confirmValue={deleteId}
          locales={{
            confirmMessage: deleteMessage,
            yes: "Yes",
            no: "No",
          }}
        />
      </>
    );
  }
}

const translatedTabs = withTranslations(defaultLocales)(UTabs);

export { translatedTabs as Tabs };

export default translatedTabs;
